import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import {Moment} from 'moment';
import * as moment from 'moment';

import {HttpBase} from 'app/services/http-base.service';
import {GraphData} from 'app/buffer-graph/models/graph-data';
import {CompareItem} from 'app/buffer-graph/models/compare-item';
import {ChartData} from 'app/buffer-graph/models/chart-data';
import {ChartDataRow} from 'app/buffer-graph/models/chart-data-row';
import {ItemOrderHistoryBase} from 'app/buffer-graph/models/item-order-history/item-order-history-base';
import {ItemOrderHistory} from 'app/buffer-graph/models/item-order-history/item-order-history';
import {ItemOrderHistoryFresh} from 'app/buffer-graph/models/item-order-history/item-order-history-fresh';
import {BufferChangeHistory} from 'app/buffer-graph/models/buffer-change-history/buffer-change-history';
import {ItemAction} from 'app/buffer-graph/models/item-actions/item-action';
import {FreshItemActionHeader} from 'app/buffer-graph/models/item-actions/fresh-item-action-header';
import {FreshItemActionLine} from 'app/buffer-graph/models/item-actions/fresh-item-action-line';
import {BufferChangeReason} from 'app/buffer-graph/models/buffer-change-reasons/buffer-change-reason';
import {SupplierSchedule} from 'app/buffer-graph/models/suppliers-schedules/supplier-schedule';
import {StateInfo} from 'app/buffer-graph/models/assortment-structure/state-info';
import {BalanceStructure} from 'app/buffer-graph/models/assortment-structure/balance-structure';
import {ChangeBufferItem} from 'app/buffer-graph/models/chage-buffer-item';
import {ColumnSettingsBase} from "../../slick-grid/models/column-settings-base";
import {TabItem} from "../models/tab-item";
import {CodeNameSummaryChart} from "../../stocks/models/code-name";
import {b} from "@angular/core/src/render3";
import {SummaryLineData} from "../models/summary-line-data";
import {ExportExcelColumn} from "../../shared/models/export-excel-column";
import {ItemOrderHistoryLine} from "../models/item-order-history/item-order-history-line";

type History = ItemOrderHistory | ItemOrderHistoryFresh;
type Action = ItemAction | FreshItemActionHeader;
type ReasonsResponse = { accessDenied: boolean, reasons: BufferChangeReason[], columns: ColumnSettingsBase[] };
type SuppliersSchedulesResponse = { accessDenied: boolean, schedules: SupplierSchedule[], columns: ColumnSettingsBase[] };
type AssortmentStructureResponse = { accessDenied: boolean, states: StateInfo[], balanceStructure: BalanceStructure };
type ChageBufferData = { accessDenied: boolean, item: ChangeBufferItem };
type ChageBufferResult = { accessDenied: boolean, success: boolean };
type SendExcel = { accessDenied: boolean, success: boolean, message: string };
type ItemOrderHistoryResponse = { accessDenied: boolean, isFresh: boolean, orders: History[], sku: string, warehouseCode: string, canSend: boolean, columns: ColumnSettingsBase[] };
type SimpleTabResponse = { accessDenied: boolean, rows: any[], sku: string, warehouseCode: string, columns: ColumnSettingsBase[] };
type BufferChangeHistoryResponse = { accessDenied: boolean, changes: BufferChangeHistory[], sku: string, warehouseCode: string, canSend: boolean, columns: ColumnSettingsBase[] };
type ActionsResponse = { accessDenied: boolean, isFresh: boolean, actions: Action[], sku: string, warehouseCode: string, canSend: boolean, columns: ColumnSettingsBase[], columnsLine: ColumnSettingsBase[] }

@Injectable()
export class BufferGraphService extends HttpBase{
    constructor(http: HttpClient, router: Router, translateService: TranslateService){
        super(http, router, translateService);

        this.tabsresizeNotifier = new Subject<void>();
        this.onTabsResized = this.tabsresizeNotifier.asObservable();
    }

    //# no use
    private readonly tabsresizeNotifier: Subject<void>;

    //# no use
    readonly onTabsResized: Observable<void>;

    // notifyTabsResize = (): void => {
    //     this.tabsresizeNotifier.next();
    // }

    //# get data for line and bubble chart
    getBufferGraphData = (itemId: number, dateStart?: Moment, dateEnd?: Moment): Observable<{ accessDenied: boolean, graphData: GraphData, skuWrhValues: CodeNameSummaryChart[], wrhCnt: number, skuCnt: number }> =>{
        // console.log(itemId, 'getBufferGraphData')
        let fDateStart = dateStart ? dateStart.format('MM/DD/YYYY') : null,
            fDateEnd = dateEnd ? dateEnd.format('MM/DD/YYYY') : null;

        return this.get<{ accessDenied: boolean, graphData: GraphData, skuWrhValues: CodeNameSummaryChart[], wrhCnt: number, skuCnt: number }>('/api/buffergraphdata/chartdata', {
            params: {
                itemId,
                dateStart: fDateStart,
                dateEnd: fDateEnd
            }
        })
            .map(data =>{
                if (!data || !data.graphData)
                    return data;

                const checkData = (val: number): number => {
                    return val >= 0 ? val : 0;
                }

                const calcBufferBulletTooltip = (r: ChartDataRow): number => {
                    return checkData(r.bufferG) - checkData(r.actionSafetyBuffer);
                }

                const calcActionBufferBulletTooltip = (r: ChartDataRow): number => {
                    return checkData(r.actionBuffer) - checkData(r.actionSafetyBuffer);
                }

                const calcActionSafetyBufferBulletTooltip = (r: ChartDataRow): number => {
                    return checkData(r.actionSafetyBuffer) - checkData(r.safetyBuffer);
                }

                const rows = data.graphData.chartData.rows.map(r => new ChartDataRow(
                    r.date,
                    checkData(r.bufferG),
                    checkData(r.bufferY),
                    checkData(r.bufferR),
                    checkData(r.safetyBuffer),
                    checkData(r.actionBuffer),
                    r.valueOut,
                    r.valueIn,
                    r.checkedBalance,
                    r.noData,
                    r.noData !== null ? 1 : 0,
                    r.balance,
                    r.targetStockLevel,
                    r.packageValue,
                    r.replenishmentTime,
                    r.shelfQuantity,
                    r.actionSafetyBuffer,
                    r.maxQuantity,
                    r.maxBuffer,
                    r.minBuffer,
                    r.qntOut1,
                    r.qntOut2,
                    r.discountQnt,
                    r.realBuff,
                    r.checkedSales,
                    r.avgToReplenishment,
                    r.transit,
                    calcBufferBulletTooltip(r),
                    calcActionBufferBulletTooltip(r),
                    calcActionSafetyBufferBulletTooltip(r),
                    r.actionBalance,
                    r.actionTransit,
                    r.cancellation ? r.cancellation : null,//r.cancellation,
                    r.lifeTime,
                    r.need,
                    r.transitNeed,
                    r.order,
                    r.balanceAmount,
                    r.saleAmount,

                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,

                    r.action,
                    r.season,
                    r.vacation,
                    r.holiday,
                    r.orderDay,
                    r.deliveryDay,
                    r.bufferGrowFixed,
                    r.bufferDecreaseFixed,
                    r.firstPriceItem,
                    r.blockedToOrder
                ));

                const chartData = new ChartData(
                    data.graphData.chartData.itemInfo,
                    rows,
                    data.graphData.chartData.min,
                    data.graphData.chartData.max,
                    data.graphData.chartData.rtMin,
                    data.graphData.chartData.rtMax,
                    moment.utc(data.graphData.chartData.startDate),
                    moment.utc(data.graphData.chartData.endDate),
                    data.graphData.chartData.visibleDaysCount,
                    data.graphData.chartData.maxDaysCount,
                    data.graphData.chartData.showAction,
                    data.graphData.chartData.showSeason,
                    data.graphData.chartData.showVacation,
                    data.graphData.chartData.showHoliday,
                    data.graphData.chartData.showOrderDay,
                    data.graphData.chartData.showDeliveryDay,
                    data.graphData.chartData.showBufferGrowFixed,
                    data.graphData.chartData.showBufferDecreaseFixed,
                    data.graphData.chartData.isMaxQuantityDisplayable,
                    data.graphData.chartData.isMaxBufferDisplayable,
                    data.graphData.chartData.showActionPriceItem,
                    data.graphData.chartData.showBlockedToOrder,
                    moment.utc(data.graphData.chartData.todayDate)
                );

                data.graphData = new GraphData(data.graphData.settings, chartData);

                return data;
            });
    }

    //# get data for summary line and bubble chart
    getSummaryBufferGraphData = (itemIds: number[], dateStart?: Moment, dateEnd?: Moment): Observable<{ accessDenied: boolean, graphData: GraphData, skuWrhValues: CodeNameSummaryChart[], wrhCnt: number, skuCnt: number }> =>{
        // console.log(itemIds, 'getSummaryBufferGraphData')
        //let itemId = itemIds[0];
        let fDateStart = dateStart ? dateStart.format('MM/DD/YYYY') : null,
            fDateEnd = dateEnd ? dateEnd.format('MM/DD/YYYY') : null;

        return this.post<{ accessDenied: boolean, graphData: GraphData, skuWrhValues: CodeNameSummaryChart[], wrhCnt: number, skuCnt: number }>('/api/buffergraphdata/summarychartdata', {
            data: {
                itemIds,
                dateStart: fDateStart,
                dateEnd: fDateEnd
            }
        })
            .map(data =>{
                if (!data || !data.graphData)
                    return data;

                const checkData = (val: number): number => {
                    return val >= 0 ? val : 0;
                }

                const calcBufferBulletTooltip = (r: ChartDataRow): number => {
                    return checkData(r.bufferG) - checkData(r.actionSafetyBuffer);
                }

                const calcActionBufferBulletTooltip = (r: ChartDataRow): number => {
                    return checkData(r.actionBuffer) - checkData(r.actionSafetyBuffer);
                }

                const calcActionSafetyBufferBulletTooltip = (r: ChartDataRow): number => {
                    return checkData(r.actionSafetyBuffer) - checkData(r.safetyBuffer);
                }

                const rows = data.graphData.chartData.rows.map(r => new ChartDataRow(
                    r.date,
                    checkData(r.bufferG),
                    checkData(r.bufferY),
                    checkData(r.bufferR),
                    checkData(r.safetyBuffer),
                    checkData(r.actionBuffer),
                    r.valueOut,
                    r.valueIn,
                    r.checkedBalance,
                    r.noData,
                    r.noData !== null ? 1 : 0,
                    r.balance,
                    r.targetStockLevel,
                    r.packageValue,
                    r.replenishmentTime,
                    r.shelfQuantity,
                    r.actionSafetyBuffer,
                    r.maxQuantity,
                    r.maxBuffer,
                    r.minBuffer,
                    r.qntOut1,
                    r.qntOut2,
                    r.discountQnt,
                    r.realBuff,
                    r.checkedSales,
                    r.avgToReplenishment,
                    r.transit,
                    calcBufferBulletTooltip(r),
                    calcActionBufferBulletTooltip(r),
                    calcActionSafetyBufferBulletTooltip(r),
                    r.actionBalance,
                    r.actionTransit,
                    r.cancellation ? r.cancellation : null,//r.cancellation,
                    r.lifeTime,
                    r.need,
                    r.transitNeed,
                    r.order,
                    r.balanceAmount,
                    r.saleAmount,

                    r.bufferGCount,
                    r.safetyBufferCount,
                    r.shelfQuantityCount,
                    r.packageValueCount,
                    r.replenishmentTimeCount,
                    r.actionBufferCount,
                    r.actionSafetyBufferCount,
                    r.maxQuantityCount,
                    r.maxBufferCount,
                    r.minBufferCount,
                    r.checkedSalesCount,
                    r.targetStockLevelCount,
                    r.checkedBalanceCount,
                    r.cancellationCount,
                    r.discountQntCount,
                    r.balanceCount,
                    r.valueInCount,
                    r.valueOutCount,
                    r.qntOut1Count,
                    r.qntOut2Count,
                    r.transitCount,
                    r.bufferYCount,
                    r.bufferRCount,
                    r.avgToReplenishmentCount,
                    r.lifeTimeCount,
                    r.needCount,
                    r.transitNeedCount,
                    r.orderCount,
                    r.balanceAmountCount,
                    r.saleAmountCount,

                    r.action,
                    r.season,
                    r.vacation,
                    r.holiday,
                    r.orderDay,
                    r.deliveryDay,
                    r.bufferGrowFixed,
                    r.bufferDecreaseFixed,
                    r.firstPriceItem,
                    r.blockedToOrder,
                ));

                const chartData = new ChartData(
                    data.graphData.chartData.itemInfo,
                    rows,
                    data.graphData.chartData.min,
                    data.graphData.chartData.max,
                    data.graphData.chartData.rtMin,
                    data.graphData.chartData.rtMax,
                    moment.utc(data.graphData.chartData.startDate),
                    moment.utc(data.graphData.chartData.endDate),
                    data.graphData.chartData.visibleDaysCount,
                    data.graphData.chartData.maxDaysCount,
                    data.graphData.chartData.showAction,
                    data.graphData.chartData.showSeason,
                    data.graphData.chartData.showVacation,
                    data.graphData.chartData.showHoliday,
                    data.graphData.chartData.showOrderDay,
                    data.graphData.chartData.showDeliveryDay,
                    data.graphData.chartData.showBufferGrowFixed,
                    data.graphData.chartData.showBufferDecreaseFixed,
                    data.graphData.chartData.isMaxQuantityDisplayable,
                    data.graphData.chartData.isMaxBufferDisplayable,
                    data.graphData.chartData.showActionPriceItem,
                    data.graphData.chartData.showBlockedToOrder,
                    moment.utc(data.graphData.chartData.todayDate)
                );

                data.graphData = new GraphData(data.graphData.settings, chartData);

                return data;
            });
    }

    //# get data for summary chart line
    getSummaryChartLineData = (lineName: string, dateStart?: Moment, dateEnd?: Moment): Observable<{ accessDenied: boolean, success: boolean, result: SummaryLineData[] }> =>{
        let fDateStart = dateStart ? dateStart.format('MM/DD/YYYY') : null,
            fDateEnd = dateEnd ? dateEnd.format('MM/DD/YYYY') : null;
        return this.post<{ accessDenied: boolean, success: boolean, result: SummaryLineData[] }>('/api/buffergraphdata/SummaryChartLineData', {
            data: {
                lineName,
                dateStart: fDateStart,
                dateEnd: fDateEnd
            }
        })
    }

    //# get data for add compare chart (itemId on CWH, and list of warehouses with activity  )
    getCompareData = (itemId: number): Observable<{ accessDenied: boolean, items: CompareItem[], cwhItemId: number }> =>{
        return this.get<{ accessDenied: boolean, items: CompareItem[], cwhItemId: number }>('/api/buffergraphdata/comparedata', {params: {itemId}});
    }

    //# get data for ItemOrderHistory tab
    getItemOrderHistory = (itemId: number, statuses: { [index: number]: string },
                           fulfillments: { [index: number]: string },
                           newMovers: { [index: number]: string },
                           module: string, table: string,
                           locale: string,
                           startDate: string, endDate: string): Observable<ItemOrderHistoryResponse> => {

        const createFresh = (info: ItemOrderHistoryFresh, id: string, isCwh?: boolean): ItemOrderHistoryFresh => {
            return new ItemOrderHistoryFresh(
                id,
                info.orderId,
                info.innerOrderId,
                info.date,
                info.planArriveDate,
                info.documentNo,
                info.order,
                info.orderType,
                info.distOrder,
                info.status,
                info.status > 0 ? statuses[info.status] : '',
                info.statusDescStyle,
                info.fulfillment,
                info.fulfillment > 0 ? fulfillments[info.fulfillment] : '',
                info.fulfillmentDescStyle,
                info.arrivalDate,
                info.arrivedQuantity,
                info.supplierCode,
                info.supplierName,
                info.buffer,
                info.safetyStock,
                info.balance,
                info.inTransit,
                info.need,
                info.hasData,
                info.hasMoq,
                info.hasMoa,
                info.extraAttribute,
                info.purchasePrice,
                info.nextInputDate,
                moment.duration(info.orderTime),
                moment.duration(info.deliveryTime),
                info.lifeTime,
                info.weekSale,
                info.lastWeekSale,
                info.changedCoeff,
                info.baseBuffer,
                info.actionQnt,
                info.newMover,
                info.newMover > 0 ? newMovers[info.newMover] : '',
                info.hasHolidayQnt,
                isCwh,
                info.crossDock ? createFresh(info.crossDock, id.toString() + '_C',true) : null);
        };

        const createNonFresh = (info: ItemOrderHistory, id: string, isCwh?: boolean): ItemOrderHistory =>{
            return new ItemOrderHistory(
                id.toString(),
                info.crossDock ? info.orderId : info.orderId,
                info.innerOrderId,
                info.date,
                info.planArriveDate,
                info.documentNo,
                info.order,
                info.orderType,
                info.distOrder,
                info.status,
                info.status > 0 ? statuses[info.status] : '',
                info.statusDescStyle,
                info.fulfillment,
                info.fulfillment > 0 ? fulfillments[info.fulfillment] : '',
                info.fulfillmentDescStyle,
                info.arrivalDate,
                info.arrivedQuantity,
                info.supplierCode,
                info.supplierName,
                info.buffer,
                info.safetyStock,
                info.balance,
                info.inTransit,
                info.need,
                info.hasData,
                info.hasMoq,
                info.hasMoa,
                info.extraAttribute,
                info.purchasePrice,
                isCwh,
                info.crossDock ? createNonFresh(info.crossDock, id.toString() + '_C', true) : null);
        };
        return this.get<ItemOrderHistoryResponse>('/api/buffergraphdata/itemorderhistory', {
            params: {
                itemId,
                module,
                table,
                localization: locale,
                startDate,
                endDate
            }
        }).map(data =>{
            if (!data || !data.orders)
                return data;
            // console.log(data.orders.map(i => i), 'itemOrderHistory')
            data.orders = data.orders.map((o, i) => data.isFresh
                ? createFresh(o as ItemOrderHistoryFresh, i.toString())
                : createNonFresh(o as ItemOrderHistory, i.toString()));

            return data;
        });
    }

    getItemOrderHistoryLine = (orderId: number): Observable<{ success: boolean, result: ItemOrderHistoryLine[] }> => {
        return this.get<{ success: boolean, result: ItemOrderHistoryLine[] }>(
            '/api/buffergraphdata/GetAdditionalOrderInfo', {params:{orderId}}
        )
    }

    //# get data for SimpleTabTest tab
    getSimpleTabData = (itemId: number, tabId: string, tabType: string,
                           module: string, table: string, procedure: string,
                            locale: string,
                           startDate: string, endDate: string): Observable<SimpleTabResponse> =>{

        return this.get<SimpleTabResponse>('/api/StocksTabs/getsimpletabdata', {
            params: {
                itemId,
                tabId,
                tabType,
                module,
                table,
                procedure,
                localization: locale,
                startDate,
                endDate
            }
        }).map(data =>{
            if (!data || !data.rows)
                return data;

            //data.rows = data.rows

            return data;
        });
    }

    //# get data for SimpleTabTest tab
    getSimpleTabDataSummary = (itemId: number[], tabId: string, tabType: string,
                        module: string, table: string, procedure: string, locale: string,
                        startDate: string, endDate: string): Observable<SimpleTabResponse> =>{

        return this.get<SimpleTabResponse>('/api/StocksTabs/getsimpletabdatasum', {
            params: {
                itemId,
                tabId,
                tabType,
                module,
                table,
                procedure,
                localization: locale,
                startDate,
                endDate
            }
        }).map(data =>{
            if (!data || !data.rows)
                return data;

            //data.rows = data.rows

            return data;
        });
    }

    //# download excel of ItemOrderHistory tab
    downloadItemOrderHistory = (columns: { header: string, field: string }[], mainRows: any[],
                                treeRows: any[], metaData: string[], culture: string,
                                mainRowsSheetName: string, treeRowsSheetName: string): Observable<Blob> =>{
        return this.post<Blob>('/api/buffergraphdata/downloadexcel', {
            data: [{
                columns,
                mainRows,
                treeRows,
                metaData,
                culture,
                mainRowsSheetName,
                treeRowsSheetName
            }]
        }, {responseType: 'blob'});
    }

    //# send excel of ItemOrderHistory tab
    sendItemOrderHistory = (sku: string, warehouseCode: string, columns: ExportExcelColumn[],
                            mainRows: any[], treeRows: any[], metaData: string[], culture: string,
                            mainRowsSheetName: string, treeRowsSheetName: string): Observable<{ success: boolean }> =>{
        return this.post<{ success: boolean }>('/api/buffergraphdata/sendexcel', {
            data: [{
                sku,
                warehouseCode,
                columns,
                mainRows,
                treeRows,
                metaData,
                culture,
                mainRowsSheetName,
                treeRowsSheetName
            }]
        });
    }

    //# get data for BufferChangeHistory tab
    getBufferChangeHistory = (itemId: number, module: string, table: string, locale: string, startDate: string, endDate: string): Observable<BufferChangeHistoryResponse> =>{
        return this.get<BufferChangeHistoryResponse>('/api/buffergraphdata/bufferchangehistory', {
            params: {
                itemId,
                module,
                table,
                localization: locale,
                startDate,
                endDate
            }
        })
            .map(data =>{
                if (data && data.changes)
                    data.changes = data.changes.map(
                        (c, i) => new BufferChangeHistory(
                            `${i}`,
                            c.dateTime,
                            c.parameter,
                            c.reason,
                            c.oldValue,
                            c.newValue
                        )
                    );//TODO: Check correctness of date
                return data;
            });
    }

    //# download excel of BufferChangeHistory tab
    downloadExcelReport = (columns: ExportExcelColumn[], mainRows: any[], culture: string, mainRowsSheetName: string): Observable<Blob> =>{
        return this.post<Blob>('/api/buffergraphdata/downloadexcel', {
            data: [{
                columns,
                mainRows,
                culture,
                mainRowsSheetName
            }]
        }, {responseType: 'blob'});
    }

    //# send excel of ItemOrderHistory tab
    sendExcelReport = (sku: string, warehouseCode: string, columns: { header: string, field: string }[],
                               mainRows: any[], culture: string, mainRowsSheetName: string): Observable<{ success: boolean }> =>{
        return this.post<{ success: boolean }>('/api/buffergraphdata/sendexcel', {
            data: [{
                sku,
                warehouseCode,
                columns,
                mainRows,
                culture,
                mainRowsSheetName
            }]
        });
    }

    //# get data for Actions tab
    getItemActions = (itemId: number, module: string, table: string, tableLine: string, locale: string, startDate: string, endDate: string): Observable<ActionsResponse> =>{
        let id = 0;
        const createFresh = (header: FreshItemActionHeader): FreshItemActionHeader =>{
            let lineId = 0;
            return new FreshItemActionHeader(
                `${id++}`,
                header.actionSeason,
                header.status,
                header.note,
                header.purchaseDate,
                //moment.utc(header.purchaseDate, this.fineDateFormat),
                header.startDate,
                header.arriveDate,
                //moment.utc(header.startDate, this.fineDateFormat),
                header.endDate,
                //moment.utc(header.endDate, this.fineDateFormat),
                header.saleChangeRatio,
                header.saleIncrease,
                header.replenishment,
                header.actionOut,
                header.importDate,
                //moment.utc(header.importDate, this.fineDateFormat),
                header.documentNumber,
                header.cancelAction,
                header.purchaseType,
                header.prevActionAvgSale,
                header.prevActionPeriod,
                header.prevActionDocumentNumber,
                header.addAdditionalStock,
                header.lines
                    ? header.lines.map(l => new FreshItemActionLine(
                    `${lineId++}`,
                    l.orderDate,
                    //moment.utc(l.orderDate, this.fineDateFormat),
                    l.arrivalDate,
                    //moment.utc(l.arrivalDate, this.fineDateTimeFormat),
                    l.nextArrivalDate,
                    //moment.utc(l.nextArrivalDate, this.fineDateFormat),
                    l.buffer,
                    l.actionBuffer,
                    l.balance,
                    l.inTransit,
                    l.order,
                    l.actionOrder,
                    ))
                    : null);
        };
        const createNonFresh = (action: ItemAction): ItemAction =>{
            return new ItemAction(
                `${id++}`,
                action.actionSeason,
                action.status,
                action.note,
                action.type,
                action.purchaseDate,
                action.startDate,
                action.arriveDate,
                action.endDate,
                action.saleChangeRatio,
                action.saleIncrease,
                action.safetyStock,
                action.averageSaleOverReplTime,
                action.bufferOld,
                action.bufferNew,
                action.bufferAfterAction,
                action.replenishment,
                action.actionOut,
                action.nestedAction,
                action.importDate,
                action.documentNumber,
                action.cancelAction,
                action.purchaseType,
                action.prevActionAvgSale,
                action.prevActionPeriod,
                action.prevActionDocumentNumber,
                action.addAdditionalStock
                );
        };
        return this.get<ActionsResponse>('/api/buffergraphdata/itemactions', {
            params: {
                itemId,
                module,
                table,
                tableLine,
                localization: locale,
                startDate,
                endDate
            }
        }).map(data =>{
            if (!data || !data.actions)
                return data;

            data.actions = data.actions.map(o => data.isFresh ? createFresh(o as FreshItemActionHeader) : createNonFresh(o as ItemAction));

            return data;
        });
    }

    //# download excel of Actions tab
    /*downloadItemActions = (columns: { header: string, field: string }[], mainRows: any[], culture: string, mainRowsSheetName: string): Observable<Blob> =>{
        return this.post<Blob>('/api/buffergraphdata/DownloadExcel', {
            data: [{
                columns,
                mainRows,
                culture,
                mainRowsSheetName
            }]
        }, {responseType: 'blob'});
    }

    //# send excel of ItemOrderHistory tab
    sendItemActions = (sku: string, warehouseCode: string, columns: { header: string, field: string }[],
                       mainRows: any[], culture: string, mainRowsSheetName: string): Observable<{ success: boolean }> =>{
        return this.post<{ success: boolean }>('/api/buffergraphdata/sendexcel', {
            data: [{
                sku,
                warehouseCode,
                columns,
                mainRows,
                culture,
                mainRowsSheetName
            }]
        });
    }*/

    //# get data for BufferChangeReasons tab
    getBufferChangeReasons = (itemId: number, module: string, table: string, locale: string, startDate: string, endDate: string): Observable<ReasonsResponse> =>{
        return this.get<ReasonsResponse>('/api/buffergraphdata/bufferchangereasons', {
            params: {
                itemId,
                module,
                table,
                localization: locale,
                startDate,
                endDate,
            }
        }).map(data =>{
            if (data && data.reasons)
                data.reasons = data.reasons.map(r => new BufferChangeReason(
                    r.date,
                    r.reason,
                    r.supplier,
                    r.lastOrderDate,
                    r.planDeliveryDate,
                    r.deliveryDate,
                    r.docInputDate,
                    r.initialOrder,
                    r.sentOrder,
                    r.arrivedOrder,
                    r.orderSchedule));

            return data;
        });
    }

    //# get data for SuppliersSchedules tab
    getSuppliersSchedules = (itemId: number, module: string, table: string, locale: string): Observable<SuppliersSchedulesResponse> =>{
        return this.get<SuppliersSchedulesResponse>('/api/buffergraphdata/suppliersschedules', {
            params: {
                itemId,
                module,
                table,
                localization: locale
            }
        }).map(data =>{
            if (data && data.schedules)
                data.schedules = data.schedules.map((s, i) => new SupplierSchedule(`${i}`,
                    s.rang,
                    s.code,
                    s.name,
                    s.schedule,
                    s.isOrderDay,
                    s.moa,
                    s.moq,
                    s.orderScheduleID));

            return data;
        });
    }

    //# get data for SuppliersSchedules tab
    getAssortmentStructure = (itemId: number): Observable<AssortmentStructureResponse> =>{
        return this.get<AssortmentStructureResponse>('/api/buffergraphdata/assortmentstructure', {params: {itemId}}).map(data =>{
            if (!data)
                return data;

            if (data.states)
                data.states = data.states.map(s => new StateInfo(s.state, s.count));

            if (data.balanceStructure){
                const struct = data.balanceStructure;
                data.balanceStructure = new BalanceStructure(struct.zeroBalance,
                    struct.safetyStockZone,
                    struct.redZone,
                    struct.yellowZone,
                    struct.greenZone,
                    struct.overstockZone);
            }

            return data;
        });
    }

    //# get data for change buffer form (not use)
    getChangeBufferData = (itemId: number): Observable<ChageBufferData> =>{
        return this.get<ChageBufferData>('/api/buffergraphdata/changebuffer', {params: {itemId}}).map(data =>{
            if (data.item)
                data.item = new ChangeBufferItem(data.item.warehouseCode, data.item.warehouseName, data.item.sku, data.item.name, data.item.buffer);

            return data;
        });
    };

    //# get item id of item
    getItemId = (warehouse: string, sku: string): Observable<{ itemId: number }> =>{
        return this.get<{ itemId: number }>('/api/buffergraphdata/getitemid', {params: {warehouse, sku}})
    };

    //# get list of tabs
    getTabList = (type: 'Main' | 'Summary'): Observable<{success: true, tabSettings: any[]}> =>{
        return this.get<{success: true, tabSettings: TabItem[]}>('/api/stockstabs/tablist', {params:{type}});
    }

    //# set data from change buffer form (not use)
    changeBuffer = (itemId: number, buffer: number, reason: string): Observable<ChageBufferResult> =>{
        return this.post<ChageBufferResult>('/api/buffergraphdata/changebuffer', {itemId, buffer, reason});
    };

    //# check has user email
    canSend = (): Observable<{ canSend: boolean }> =>{
        return this.get<{ canSend: boolean }>('/api/buffergraphdata/cansend')
    };

    //# save opened tabs data to one excel
    downloadViewMode = (data: any[]): Observable<Blob> =>{
        return this.post<Blob>('/api/buffergraphdata/downloadexcel', {data}, {responseType: 'blob'});
    }

    //# send to email opened tabs data to one excel
    sendViewMode = (data: any[]): Observable<SendExcel> =>{
        return this.post<SendExcel>('/api/buffergraphdata/sendexcel', {data});
    }
}



// WEBPACK FOOTER //
// ./src/app/buffer-graph/services/buffer-graph.service.ts